import { useEffect, useState } from 'react';
import { Box, FormControlLabel, Link, Switch, TextField, Typography } from '@mui/material';
import { Grid } from '@mui/system';
import { ConditionalLoader, Section, ValuationStatusTracker } from 'dashboard409a/pages/Operations/components';
import Avatar from 'dashboard409a/pages/Operations/components/Avatar/Avatar';
import formatCurrency from 'dashboard409a/utilities/formatCurrency';
import { DEFAULT_VISIBLE_FIELDS, getFinancialFields } from './ValuedInformation.constants';
import { ValuedInformationProps } from './ValuedInformation.props';
import { Container, Field, FinancialField, FinancialFieldsGrid, HeaderRow, Label, PersonnelRow, StatusBadge, StyledLinkSpan, ToggleContainer, UserSection, Value } from './ValuedInformation.styles';

const ValuedInformation = ({ 
  isFromModels,
  valuedInformation, 
  isLoading,
  statusOptions,
  currentStatusId,
  onStatusChange,
  isStatusLoading = false,
  companyFolder,
  valuationFolder,
  onUpdateValuationFolder,
}: ValuedInformationProps) => {
  const [isExpanded, setIsExpanded] = useState(!isFromModels);
  const [isEditingValuationFolder, setIsEditingValuationFolder] = useState(false);
  const [valuationFolderValue, setValuationFolderValue] = useState(valuationFolder || '');
  const [isSavingValuationFolder, setIsSavingValuationFolder] = useState(false);

  // Update local state when valuationFolder prop changes
  useEffect(() => {
    if (valuationFolder !== undefined) {
      setValuationFolderValue(valuationFolder || '');
    }
  }, [valuationFolder]);

  const handleValuationFolderBlur = async () => {
    if (!onUpdateValuationFolder) {
      return;
    }
    
    const folderName = valuationFolderValue.trim();
    // Extract just the folder name (remove /Valuations/{companyFolder}/ prefix if present)
    let folderNameOnly = folderName;
    if (companyFolder) {
      folderNameOnly = folderName.replace(new RegExp(`^/?Valuations/?${companyFolder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/?`), '').trim();
    } else {
      folderNameOnly = folderName.replace(/^\/?Valuations\/?/, '').trim();
    }
    
    // If the value hasn't changed, just exit edit mode
    if (folderNameOnly === (valuationFolder || '')) {
      setIsEditingValuationFolder(false);
      return;
    }

    setIsSavingValuationFolder(true);
    try {
      await onUpdateValuationFolder(folderNameOnly);
      setIsEditingValuationFolder(false);
    } catch {
      // Error is handled by the parent component
    } finally {
      setIsSavingValuationFolder(false);
    }
  };

  const handleValuationFolderKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleValuationFolderBlur();
    } else if (e.key === 'Escape') {
      setValuationFolderValue(valuationFolder || '');
      setIsEditingValuationFolder(false);
    }
  };

  const getValuationFolderPath = (folderValue: string) => {
    if (companyFolder && folderValue) {
      return `/Valuations/${companyFolder}/${folderValue}`;
    }
    if (folderValue) {
      return `/Valuations/${folderValue}`;
    }
    if (companyFolder) {
      return `/Valuations/${companyFolder}/`;
    }
    return '/Valuations/';
  };

  // Display full path: /Valuations/{companyFolder}/{valuationFolder}
  let displayValuationFolderPath = '';
  if (companyFolder && valuationFolder) {
    displayValuationFolderPath = `/Valuations/${companyFolder}/${valuationFolder}`;
  } else if (valuationFolder) {
    displayValuationFolderPath = `/Valuations/${valuationFolder}`;
  }

  const allFields = getFinancialFields(valuedInformation);

  // Filter fields based on toggle state
  const visibleFields = allFields.filter(field =>
    isExpanded || DEFAULT_VISIBLE_FIELDS.includes(field.key as typeof DEFAULT_VISIBLE_FIELDS[number])
  );

  return (
    <Section title="Valuation">
      <Container>
        <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
          {/* Left Column - Main Content */}
          <Grid size={{ md: 8, sm: 12 }}>
            <HeaderRow>
              <Field>
                <ConditionalLoader isLoading={isLoading} width={150}>
                  <Link href={valuedInformation?.valuationUrl} target="_blank" rel="noopener noreferrer">
                    <StyledLinkSpan>{valuedInformation?.valuationName || '-'}</StyledLinkSpan>
                  </Link>
                </ConditionalLoader>
              </Field>
              <ConditionalLoader isLoading={isLoading} width={150}>
                <StatusBadge>{valuedInformation?.bookingType || '-'}</StatusBadge>
              </ConditionalLoader>
            </HeaderRow>
            {valuedInformation?.valuationNotice && (
              <span>NOTICE: {valuedInformation.valuationNotice}</span>
            )}
            <Box mt={0.5} />
            <ToggleContainer>
              <FormControlLabel
                control={
                  <Switch
                    checked={isExpanded}
                    onChange={e => setIsExpanded(e.target.checked)}
                    size="small"
                  />
                }
                label={isExpanded ? 'Show Less' : 'Show More'}
              />
            </ToggleContainer>
            <FinancialFieldsGrid>
              {visibleFields.map(field => {
                const value = field.getValue();
                let displayValue: string = '-';
                if (field.isCurrency) {
                  if (value !== undefined && value !== null && value !== '-') {
                    displayValue = formatCurrency({
                      value: typeof value === 'number' ? value : Number(value) || 0,
                    });
                  }
                } else {
                  displayValue = (value as string) || '-';
                }
                return (
                  <FinancialField key={field.key}>
                    <Label>{field.label}</Label>
                    <ConditionalLoader isLoading={isLoading} width={150}>
                      <Value>{displayValue}</Value>
                    </ConditionalLoader>
                  </FinancialField>
                );
              })}
            </FinancialFieldsGrid>
            <PersonnelRow>
              <Field>
                <Label>Valuation Lead</Label>
                <UserSection>
                  <Avatar
                    src={valuedInformation?.valuationLeadImageSrc || ''}
                    name={valuedInformation?.valuationLeadName || '-'}
                    roleTitle="Valuation Lead"
                    isLoading={isLoading}
                  />
                </UserSection>
              </Field>
              <Field>
                <Label>Engagement Manager</Label>
                <UserSection>
                  <Avatar
                    src={valuedInformation?.valuationEngagementManagerImageSrc || ''}
                    name={valuedInformation?.valuationEngagementManager || '-'}
                    roleTitle={valuedInformation?.valuationEngagementManagerRole || '-'}
                    isLoading={isLoading}
                  />
                </UserSection>
              </Field>
            </PersonnelRow>
            <Field sx={{ mt: 2 }}>
              <Label>Dropbox: Valuation Folder</Label>
              <ConditionalLoader isLoading={isLoading} width={300}>
                {isEditingValuationFolder ? (
                  <TextField
                    value={getValuationFolderPath(valuationFolderValue)}
                    onChange={e => {
                      const { value } = e.target;
                      // Remove /Valuations/{companyFolder}/ prefix for editing
                      let folderName = value;
                      if (companyFolder) {
                        folderName = value.replace(new RegExp(`^/?Valuations/?${companyFolder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/?`), '').trim();
                      } else {
                        folderName = value.replace(/^\/?Valuations\/?/, '').trim();
                      }
                      setValuationFolderValue(folderName);
                    }}
                    onBlur={handleValuationFolderBlur}
                    onKeyDown={handleValuationFolderKeyDown}
                    disabled={isSavingValuationFolder}
                    size="small"
                    fullWidth
                    autoFocus
                    sx={{ mt: 0.5 }}
                  />
                ) : (
                  <Typography
                    onClick={() => {
                      if (onUpdateValuationFolder) {
                        setIsEditingValuationFolder(true);
                      }
                    }}
                    sx={{
                      mt: 0.5,
                      cursor: onUpdateValuationFolder ? 'pointer' : 'default',
                      color: onUpdateValuationFolder ? 'primary.main' : 'text.primary',
                      '&:hover': onUpdateValuationFolder ? { textDecoration: 'underline' } : {},
                    }}
                  >
                    {displayValuationFolderPath || '-'}
                  </Typography>
                )}
              </ConditionalLoader>
            </Field>
          </Grid>

          {/* Right Column - Status Tracker */}
          {statusOptions && statusOptions.length > 0 && (
            <Grid size={{ md: 4, sm: 12 }} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box>
                <Typography variant="body1" fontWeight={600} sx={{ fontSize: '0.875rem' }}>Status</Typography>
              </Box>
              <Box mt={1} sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <ValuationStatusTracker
                  statusOptions={statusOptions}
                  currentStatusId={currentStatusId}
                  onStatusChange={onStatusChange}
                  isLoading={isStatusLoading}
                  isExpanded={isExpanded}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </Container>
    </Section>
  );
};

export default ValuedInformation;
